import { Loader } from '@googlemaps/js-api-loader'
import BrowserStorage from '@helpers/browser-storage'
import { createTemplate } from '@helpers/create-template.js'
import EventBus from '@helpers/EventBus'
import { snakeToCamel } from '@helpers/string-helper'


const template: HTMLTemplateElement | null = createTemplate(`
    <template>
        <style>
            .google-map {
                position: relative;
                overflow: hidden;
                width: 100%;
                height: 100%;
                min-height: 650px
            }
            
            .google-map > .map {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
            }
            .google-map > .map p {
                font-size: 16px;
                line-height: 1.5em;
            }
        </style>
        
        <div class="google-map">
            <div class="js-map map"></div>
        </div>
        
    </template>
`)

export default class GoogleMap extends HTMLElement {
    private loader: any // Define loader property
    private mapContainer: HTMLElement

    //Properties
    private lat: number
    private long: number
    private apiKey: string
    private mapId: string
    private zoom: number
    private info: string

    /**
     * Add all attributes ot you web component.
     * For attributeChangedCallback() callback to fire when an attribute changes, you have to observe the attributes.
     */
    static get observedAttributes() {
        return [
            'lat',
            'long',
            'api-key',
            'map-id',
            'zoom',
            'info',
        ]
    }

    /**
     * The browser calls this method when the element is
     * added to the DOM.
     */
    connectedCallback() {
        console.log('Google Map: Connected')
        this.appendChild(template.content.cloneNode(true))
        this.updateTextsfromAttributes()
        this.initializeComponent()
    }

    /**
     * Called before connectedCallback() for every observed attribute
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue !== newValue) {
            for (const item of GoogleMap.observedAttributes) {
                if (item === name) {
                    const propertyName = snakeToCamel(item)
                    this[propertyName] = newValue //Set text in property
                }
            }
        }
        this.updateTextsfromAttributes()
    }

    /**
     * Sets texts of tags with a data-attribute with webcomponents attribute value
     */
    updateTextsfromAttributes() {
        GoogleMap.observedAttributes.forEach((item) => {
            const propertyName = snakeToCamel(item)
            const selector = '[data-attribute=' + item + ']'
            this.querySelectorAll(selector).forEach((item) => {
                item.innerHTML = this[propertyName] //Get text from property and write to HTML
            })
        })
    }

    initializeComponent() {
        //Elements
        this.mapContainer = this.querySelector('.js-map')

        //Properties
        this.lat = Number(this['lat'])
        this.long = Number(this['long'])
        this.apiKey = this['apiKey']
        this.mapId = this['mapId']
        this.zoom = Number(this['zoom']);
        this.info = this['info']

        this.loadMap()
    }

    loadMap() {

        // Load Google Maps API
        this.loader = new Loader({
            apiKey: this.apiKey,
            version: 'weekly', // or specify a version (e.g., '3.45')
            libraries: ['marker'], // Ensure the marker library is loaded
        })

        this.loader
            .load()
            .then(() => {
                console.log('Google Map: Google Maps API loaded')
                this.createMap()
            })
            .catch((error) => {
                console.error(
                    'Google Map: Failed to load Google Maps API',
                    error,
                )
                return
            })

    }

    createMap() {
        // Create Map
        const map = new google.maps.Map(this.mapContainer, {
            gestureHandling: 'cooperative',
            clickableIcons: false,
            zoom: this.zoom,
            center: { lat: this.lat, lng: this.long },
            mapId: this.mapId,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: true,
            fullscreenControl: true
        });

        const pinSvg = `
        <svg width="28" height="46" viewBox="0 0 20 36" xmlns="http://www.w3.org/2000/svg">
          <path fill="#2d7373" d="M10,0.5c5.2,0,9.5,4.3,9.5,9.5S10,35.2,10,35.2S0.5,15.2,0.5,10S4.8,0.5,10,0.5z"/>
          <path fill="#ffffff" d="M10,5.5c1.9,0,3.5,1.6,3.5,3.5s-1.6,3.5-3.5,3.5S6.5,10.9,6.5,9S8.1,5.5,10,5.5z"/>
        </svg>
    `;

        const marker = new google.maps.marker.AdvancedMarkerElement({
            map: map,
            position: { lat: this.lat, lng: this.long },
            content: new DOMParser().parseFromString(pinSvg, "image/svg+xml").documentElement
        });

        // Create Info Window with Google Maps link
        if(this.info) {
            const googleMapsUrl = `https://www.google.com/maps?q=${this.lat},${this.long}`;
            const infoContent = `
                <div>
                    ${this.info}
                    <p style="margin-top: 10px;">
                        <a href="${googleMapsUrl}" target="_blank" style="color: #2d7373; text-decoration: none;">
                            In Google Maps öffnen
                        </a>
                    </p>
                </div>
            `;

            const infoWindow = new google.maps.InfoWindow({
                content: infoContent
            });

            infoWindow.open(map, marker);
            google.maps.event.addListener(marker, "click", () =>
                infoWindow.open(map, marker)
            );
        }
    }

}
